import { useCallback, useEffect, useRef, useState } from 'react'
import { Trace } from '../lib/trace'
import type { ReactNode } from 'react'
import { api, unlock, type GateState } from '../api'
import { radius, theme, TAP_MIN } from '../theme'
import { Mascot } from './Mascot'
import { signInWithGoogle, explainSignInProblem } from '../lib/googleSignIn'
import { sfx } from '../lib/sound'
import { english, fromServer, t } from '../lib/i18n'

/**
 * The grown-up PIN (D-065, 8 Oct 2026).
 *
 * Asked before Settings opens and before a child's PIN can be changed. It
 * replaced a question asking for the grown-up's birth year, which any child
 * who knows how old their parents are could answer.
 *
 * Three ways through, depending on what the server says:
 *
 *   * **A PIN exists**: type it (4 to 6 numbers). "Forgot it?" below.
 *   * **None yet**: the birth-year question once, then choose a PIN twice.
 *     The year is still asked so a child who finds Settings first cannot
 *     simply make one; and the grown-up is emailed when one is made.
 *   * **Forgot it**: sign in with Google again. That needs the grown-up's
 *     Google password, which is the one thing a child at the tablet does not
 *     have. On a laptop (no Google) the birth year stands in. Then make a new
 *     one.
 */
type Step = 'loading' | 'enter' | 'year' | 'choose' | 'confirm' | 'forgot'

export function GrownUpGate({
  why, onPass, onCancel,
}: { why: string; onPass: () => void; onCancel: () => void }) {
  const [step, setStep] = useState<Step>('loading')
  const [year, setYear] = useState('')
  const [first, setFirst] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const [gate, setGate] = useState<GateState | null>(null)
  const [busy, setBusy] = useState(false)
  // How many digits the grown-up PIN has, when the server knows. With it the
  // pad goes straight in on the last digit, no check button (Sam, 8 Oct).
  const [length, setLength] = useState<number | null>(null)

  // Held in a ref: the caller passes a new function every render, and an
  // effect that depended on it would ask the server again on every render.
  const passRef = useRef(onPass)
  passRef.current = onPass
  useEffect(() => {
    if (unlock.hasGrownUp()) { passRef.current(); return }
    api.grownUp().then((g) => { setLength(g.pin_length); setStep(g.has_pin ? 'enter' : 'year') })
      .catch(() => setStep('enter'))
    api.state().then(setGate).catch(() => undefined)
  }, [])

  const fail = (err: unknown, fallback: string) =>
    setMessage(fromServer(err, fallback))

  const enter = async (pin: string) => {
    setBusy(true)
    try {
      const { ticket } = await api.unlockGrownUp(pin)
      unlock.setGrownUp(ticket)
      sfx.correct()
      onPass()
    } catch (err) {
      fail(err, t('That did not work. Try again.'))
      throw err
    } finally {
      setBusy(false)
    }
  }

  const make = async (pin: string) => {
    if (pin !== first) {
      setMessage(t('Those two were different. Choose again.'))
      setFirst('')
      setStep('choose')
      return
    }
    setBusy(true)
    try {
      const { ticket } = await api.makeGrownUpPin(pin, year)
      unlock.setGrownUp(ticket)
      sfx.correct()
      onPass()
    } catch (err) {
      fail(err, t('That did not work.'))
      setFirst('')
      setStep('choose')
    } finally {
      setBusy(false)
    }
  }

  const forgot = async () => {
    setBusy(true)
    setMessage(null)
    try {
      // Before Google's window, not after: on a child's tablet the reset must
      // not sign Google in at all (22 Oct 2026).
      if (gate?.parent?.device_label) {
        throw Object.assign(new Error('child_device'), { detail: 'child_device' })
      }
      if (gate?.auth_mode === 'google' && gate.firebase) {
        const token = await signInWithGoogle(gate.firebase)
        await api.forgotGrownUp({ token })
      } else {
        await api.forgotGrownUp({ answer: year })
      }
      setStep('choose')
    } catch (err) {
      const detail = (err as { detail?: string } | null)?.detail
      setMessage(detail === 'child_device'
        // Resetting needs Google, which a child's tablet never signs in to.
        ? t("This is a child's tablet. Clear the grown-up PIN from Settings on your own phone.")
        : t(detail || explainSignInProblem(err)))
    } finally {
      setBusy(false)
    }
  }

  const checkYear = () => {
    const age = new Date().getFullYear() - Number(year)
    if (!/^\d{4}$/.test(year) || age < 18 || age > 100) {
      setMessage(t("That is not a grown-up's year."))
      return
    }
    setMessage(null)
    setStep('choose')
  }

  const local = gate?.auth_mode !== 'google'

  return (
    <Modal label={t('Grown-up PIN')} onCancel={onCancel} grownUp>
      <div style={{ display: 'grid', placeItems: 'center', marginBottom: 2 }}>
        <Mascot size={58} mood="wonder" label="" />
      </div>
      <h2 style={{ margin: '4px 0 2px', fontSize: 22, color: theme.text }}>
        <Trace id="T27.pin_hash" writes title="the grown-up PIN, stored only as a hash; a right one saves a 30-minute ticket to T28 access_grant">
          {t('Just for grown-ups')}
        </Trace>
      </h2>
      <p style={{ margin: '0 0 12px', color: theme.muted, fontSize: 15 }}>{why}</p>

      {step === 'loading' ? <p style={{ color: theme.muted }}>{t('One moment…')}</p> : null}

      {step === 'enter' ? (
        <>
          <DigitEntry
            grownUp
            prompt={t('Type the grown-up PIN.')}
            min={length ?? 4} max={length ?? 6}
            busy={busy}
            message={message}
            onDigit={() => setMessage(null)}
            onSubmit={enter}
          />
          <LinkButton onClick={() => { setMessage(null); setStep('forgot') }}>{t('Forgot it?')}</LinkButton>
        </>
      ) : null}

      {step === 'year' ? (
        <YearAsk
          intro={t("First, make a grown-up PIN. It keeps Settings, and each child's PIN, for grown-ups.")}
          year={year} setYear={(y) => { setYear(y); setMessage(null) }}
          message={message} onNext={checkYear} />
      ) : null}

      {step === 'choose' ? (
        <DigitEntry
          key="choose"
          grownUp
          prompt={t('Choose a grown-up PIN, 4 to 6 numbers.')}
          min={4} max={6}
          busy={busy}
          message={message}
          onDigit={() => setMessage(null)}
          onSubmit={async (pin) => { setFirst(pin); setStep('confirm') }}
        />
      ) : null}

      {step === 'confirm' ? (
        <DigitEntry
          key="confirm"
          grownUp
          prompt={t('Type it once more.')}
          min={first.length || 4} max={first.length || 6}
          busy={busy}
          message={message}
          onDigit={() => setMessage(null)}
          onSubmit={make}
        />
      ) : null}

      {step === 'forgot' ? (
        local ? (
          <YearAsk
            intro={t('To make a new grown-up PIN, answer this first.')}
            year={year} setYear={(y) => { setYear(y); setMessage(null) }}
            message={message} onNext={forgot} />
        ) : (
          <div>
            <p style={{ margin: '0 0 12px', fontSize: 15, color: theme.text2, lineHeight: 1.5 }}>
              {t('Sign in with your Google account again. Then you can make a new grown-up PIN.')}
            </p>
            {message ? <p style={{ color: theme.orangeDark, fontWeight: 700, fontSize: 15 }}>{message}</p> : null}
            <BigButton onClick={() => void forgot()} disabled={busy}>
              {busy ? t('One moment…') : t('Sign in with Google')}
            </BigButton>
          </div>
        )
      ) : null}
    </Modal>
  )
}

function YearAsk({
  intro, year, setYear, message, onNext,
}: {
  intro: string; year: string; setYear: (y: string) => void
  message: string | null; onNext: () => void
}) {
  return (
    <div>
      <p style={{ margin: '0 0 12px', fontSize: 15, color: theme.text2, lineHeight: 1.5 }}>{intro}</p>
      <label style={{ display: 'block', fontSize: 15, color: theme.muted, marginBottom: 6 }}>
        {t('What year were you born?')}
      </label>
      <input
        value={year}
        inputMode="numeric"
        placeholder="1985"
        aria-label={t('Year you were born')}
        onChange={(e) => setYear(e.target.value.replace(/\D/g, '').slice(0, 4))}
        onKeyDown={(e) => { if (e.key === 'Enter') onNext() }}
        autoFocus
        style={{
          width: '100%', boxSizing: 'border-box', textAlign: 'center', fontSize: 24,
          letterSpacing: 3, padding: '10px 12px', borderRadius: radius.md,
          border: `1.5px solid ${theme.border}`, background: theme.card, color: theme.text,
          fontFamily: 'inherit',
        }}
      />
      {message ? <p style={{ color: theme.orangeDark, fontWeight: 700, fontSize: 15, margin: '10px 0 0' }}>{message}</p> : null}
      <div style={{ marginTop: 14 }}>
        <BigButton onClick={onNext} disabled={year.length !== 4}>{t('Next')}</BigButton>
      </div>
    </div>
  )
}

// ------------------------------------------------------------- shared bits

/**
 * A number pad with dots. `min === max` submits as soon as it is full (a
 * child's 4-digit PIN); otherwise a ✓ key submits (the grown-up's 4 to 6).
 * Keyboard digits work too. `grownUp` translates its two key names; a
 * child's PIN pad stays English (lib/i18n.ts).
 */
export function DigitEntry({
  prompt, min, max, busy, message, onSubmit, onDigit, grownUp = false,
}: {
  grownUp?: boolean
  prompt: string
  min: number
  max: number
  busy?: boolean
  message?: string | null
  onSubmit: (pin: string) => Promise<void> | void
  onDigit?: () => void
}) {
  const [digits, setDigits] = useState('')
  const [shake, setShake] = useState(false)
  const tr = grownUp ? t : english

  const submit = useCallback(async (pin: string) => {
    try {
      await onSubmit(pin)
    } catch {
      setShake(true)
      window.setTimeout(() => setShake(false), 450)
    }
    setDigits('')
  }, [onSubmit])

  const press = useCallback((d: string) => {
    if (busy || digits.length >= max) return
    onDigit?.()
    const next = digits + d
    setDigits(next)
    if (min === max && next.length === max) void submit(next)
  }, [busy, digits, max, min, onDigit, submit])

  const back = useCallback(() => setDigits((cur) => cur.slice(0, -1)), [])
  const done = useCallback(() => {
    if (digits.length >= min && !busy) void submit(digits)
  }, [digits, min, busy, submit])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      if (target && target.tagName === 'INPUT') return
      if (/^[0-9]$/.test(e.key)) press(e.key)
      else if (e.key === 'Backspace') back()
      else if (e.key === 'Enter') done()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [press, back, done])

  const key = (label: string, action: () => void, aria?: string, disabled = false) => (
    <button
      key={label}
      type="button"
      onClick={action}
      disabled={busy || disabled}
      aria-label={aria ?? label}
      style={{
        height: 58, minWidth: TAP_MIN, borderRadius: radius.xl,
        border: `2px solid ${theme.borderWarm}`, background: theme.card,
        fontFamily: "'Fredoka', sans-serif", fontSize: 26, fontWeight: 600,
        color: disabled ? theme.faint : theme.text, cursor: 'pointer',
        boxShadow: `0 3px 0 ${theme.borderWarm}`,
      }}
    >{label}</button>
  )

  return (
    <div>
      <p style={{ margin: '0 0 10px', color: theme.text2, fontSize: 15, fontWeight: 700 }}>{prompt}</p>
      <style>{`@keyframes ws-shake2 { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-10px)}
        40%{transform:translateX(9px)} 60%{transform:translateX(-6px)} 80%{transform:translateX(4px)} }`}</style>
      <div style={{
        display: 'flex', justifyContent: 'center', gap: 12, marginBottom: 8,
        animation: shake ? 'ws-shake2 0.42s ease' : undefined,
      }}>
        {Array.from({ length: max }, (_, i) => (
          <span key={i} style={{
            width: 16, height: 16, borderRadius: 999,
            background: i < digits.length ? theme.green : 'transparent',
            border: `2.5px solid ${i < digits.length ? theme.green : i < min ? theme.border : theme.borderSoft}`,
          }} />
        ))}
      </div>
      <p role="status" style={{ minHeight: 22, margin: '0 0 8px', color: theme.orangeDark, fontSize: 15, fontWeight: 700 }}>
        {message ?? ''}
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 9 }}>
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => key(d, () => press(d)))}
        {key('←', back, tr('Delete'))}
        {key('0', () => press('0'))}
        {min === max ? <span /> : key('✓', done, tr('Done'), digits.length < min)}
      </div>
    </div>
  )
}

export function Modal({
  label, children, onCancel, grownUp = false,
}: { label: string; children: ReactNode; onCancel: () => void; grownUp?: boolean }) {
  return (
    <div role="dialog" aria-modal="true" aria-label={label} style={{
      position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(21,32,43,0.45)',
      display: 'grid', placeItems: 'center', padding: 16, overflowY: 'auto',
    }}>
      <div style={{
        background: theme.bg, borderRadius: 24, padding: '18px 20px 14px',
        width: '100%', maxWidth: 350, textAlign: 'center',
        boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
      }}>
        {children}
        <LinkButton onClick={onCancel} muted>{grownUp ? t('Cancel') : 'Cancel'}</LinkButton>

      </div>
    </div>
  )
}

function LinkButton({ children, onClick, muted }: { children: ReactNode; onClick: () => void; muted?: boolean }) {
  return (
    <button type="button" onClick={onClick} style={{
      marginTop: 8, background: 'transparent', border: 'none',
      color: muted ? theme.muted : theme.blue, fontSize: 15, fontWeight: 700,
      cursor: 'pointer', minHeight: TAP_MIN, fontFamily: 'inherit',
    }}>{children}</button>
  )
}

function BigButton({ children, onClick, disabled }: { children: ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} style={{
      width: '100%', minHeight: 50, borderRadius: radius.pill, border: 'none',
      background: disabled ? theme.border : theme.blue, color: '#fff', fontWeight: 800,
      fontSize: 17, cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit',
    }}>{children}</button>
  )
}
